/**
 * Free Cologne Promo — Spend R7,000, Get a Free Cologne
 * TEST BUILD. Presentation + UX only. The R0 price itself comes from a native
 * Shopify "Buy X Get Y" automatic discount configured in Admin — this file does
 * not set or fake any price.
 *
 * v2 (2026-09-09):
 *  1. A bold, bouncing message at every stage (not just once qualified) and a
 *     gold-shine animation on the choose/change-cologne button once a
 *     customer qualifies — no emoji, matches the theme's own font.
 *  2. A compulsory picker: if a customer who qualifies tries to check out
 *     without having picked a free cologne yet, checkout is paused and a modal
 *     forces them to pick one before the checkout click is allowed through.
 *     There is deliberately no "skip"/"continue without" option — closing the
 *     dialog only cancels that checkout attempt, it does not let it proceed.
 *  3. window.fetch is patched so the widget refreshes on every cart change —
 *     add/change/update/clear — no matter which script on the page triggered
 *     it, so it can't go stale (e.g. adding more of an item already in the
 *     cart, or using the qty +/- steppers).
 *
 * v3 (2026-09-09) — confirmed via a real order (paid via "Stitch Express", a
 * one-click accelerated checkout button) that a qualifying cart checked out
 * with no cologne at all: those buttons skip the cart page's "Check out"
 * button entirely, so the v2 gate never got a chance to run. Since those
 * buttons genuinely cannot be intercepted by theme code (see limits below),
 * the fix is maybeAutoPrompt(): the offer now pops up automatically the
 * instant a cart qualifies, on whatever page that happens, once per browser
 * session — not gated on any checkout click at all. This does not guarantee
 * 100% coverage (a customer can still dismiss it and click an express button
 * immediately after), but it puts the offer in front of them before they
 * reach for any checkout button, instead of only at a click this store's
 * checkout methods don't all route through.
 *
 * v4 (2026-09-11) — a customer could add a sale item, watch the "You're RX
 * away" message count it toward the R7,000, then never actually qualify at
 * checkout, because the real "Spend R7,000, Get a Free Cologne" discount
 * (server-side) only counts full-price items toward the threshold. This
 * file's own total was computed from raw cart.js data, which has no
 * compare-at-price field, so it had no way to know a line was on sale — it
 * silently counted everything, disagreeing with the backend. Fixed by
 * cross-referencing each cart line against its product's real variant data
 * (which does carry compare_at_price) before totalling, so the number shown
 * here always matches what the real discount will do.
 *
 * v5 (2026-09-15) — the v4 fix only caught discounts recorded as a product's
 * own compare_at_price. A bundle app (Fast Bundle) discounts its cart lines
 * at the cart level instead — the two component products it adds are not
 * independently on sale, so /products/{handle}.js showed no compare_at_price
 * for them and v4's check let their discounted lines count fully toward the
 * R7,000 threshold. Fixed by dropping the per-product lookup entirely and
 * checking each cart line's own discount fields straight from /cart.js
 * (final_line_price vs original_line_price / total_discount) — this is set
 * by Shopify for any line-level discount regardless of source (native sale,
 * Buy X Get Y, Fast Bundle, or any future app), so it can't miss one the way
 * a compare-at-only check could, and it needs no extra network calls.
 *
 * Known platform limits (Shopify Advanced plan, not Plus):
 *  - This can gate the normal "Checkout" button on the cart page. It cannot gate
 *    Shop Pay / PayPal / other accelerated "dynamic checkout" buttons, or a
 *    "Buy it now" button — Shopify does not allow theme code to intercept those,
 *    and several render as cross-origin iframes no page script can touch at all.
 *  - Shopify's own hosted checkout page (after the customer leaves the cart)
 *    cannot show custom popups on a non-Plus plan. Everything here runs on the
 *    cart step, before checkout, which is the furthest point theme code reaches.
 */
(function () {
  var THRESHOLD = 7000; // Rand
  var GIFT_PROPERTY_KEY = 'Gift';
  var GIFT_PROPERTY_VALUE = 'Free Cologne';
  var GATE_BYPASS_FLAG = '__freeCologneBypass';

  var pendingForm = null;
  var pendingSubmitter = null;
  var lastCartState = null;

  function formatMoney(amount) {
    return 'R' + Math.ceil(amount).toLocaleString('en-ZA');
  }

  // ---------- Modal (self-mounting, one shared instance for the whole site) ----------

  function ensureModal() {
    var existing = document.getElementById('free-cologne-modal');
    if (existing) return existing;

    var modal = document.createElement('div');
    modal.id = 'free-cologne-modal';
    modal.className = 'free-cologne-modal';
    modal.hidden = true;
    modal.innerHTML =
      '<div class="free-cologne-modal__backdrop" data-free-cologne-close></div>' +
      '<div class="free-cologne-modal__content" role="dialog" aria-modal="true" aria-labelledby="free-cologne-modal-title">' +
      '  <button type="button" class="free-cologne-modal__close" data-free-cologne-close aria-label="Close">&times;</button>' +
      '  <h2 id="free-cologne-modal-title">Choose Your Free Cologne</h2>' +
      '  <p id="free-cologne-modal-intro" class="free-cologne-modal__intro"></p>' +
      '  <div class="free-cologne-modal__grid" id="free-cologne-modal-grid">' +
      '    <p class="free-cologne-modal__loading">Loading colognes…</p>' +
      '  </div>' +
      '</div>';
    document.body.appendChild(modal);

    // Deliberately no "skip"/"continue without" control in compulsory mode:
    // closing the dialog (X or backdrop) only cancels this checkout attempt,
    // it does not submit the form. The only way past the gate is to pick a
    // cologne — see attachSelectButtons, which is the sole path that calls
    // submitFormBypassingGate for a gated checkout attempt.
    modal.addEventListener('click', function (e) {
      if (e.target.hasAttribute('data-free-cologne-close')) closeModal();
    });

    loadColognesIntoModal(modal);

    return modal;
  }

  function loadColognesIntoModal(modal) {
    var grid = modal.querySelector('#free-cologne-modal-grid');
    fetch('/collections/colognes-collection/products.json?limit=20')
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var products = (data && data.products) || [];
        if (!products.length) {
          grid.innerHTML = '<p>No colognes are available to select right now — please contact us.</p>';
          return;
        }
        var html = '';
        products.forEach(function (product) {
          var variant = product.variants && product.variants[0];
          if (!variant || !variant.available) return;
          var image = product.images && product.images[0] ? product.images[0].src : '';
          html +=
            '<div class="free-cologne-option" data-variant-id="' +
            variant.id +
            '">' +
            (image
              ? '<img src="' + image + '&width=200" alt="' + escapeHtml(product.title) + '" loading="lazy" width="120" height="120">'
              : '') +
            '<p class="free-cologne-option__title">' +
            escapeHtml(product.title) +
            '</p>' +
            '<p class="free-cologne-option__price"><s>' +
            formatMoney(variant.price) + // /products.json prices are already in Rand, not cents like /cart.js
            '</s> <strong>FREE</strong></p>' +
            '<button type="button" class="button free-cologne-select-btn">Select</button>' +
            '</div>';
        });
        grid.innerHTML = html || '<p>No colognes are available to select right now — please contact us.</p>';
        attachSelectButtons(grid);
      })
      .catch(function () {
        grid.innerHTML = '<p>Could not load the cologne list — please refresh and try again.</p>';
      });
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function attachSelectButtons(grid) {
    var buttons = grid.querySelectorAll('.free-cologne-select-btn');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function (e) {
        var btn = e.currentTarget;
        var option = btn.closest('.free-cologne-option');
        var variantId = option.getAttribute('data-variant-id');
        var formToResume = pendingForm;
        var submitterToResume = pendingSubmitter;
        btn.disabled = true;
        btn.textContent = 'Adding…';

        fetch('/cart.js')
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            var existingFree = findGiftLineItem(cart);
            if (existingFree) {
              return fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line: existingFree.key, quantity: 0 }),
              });
            }
          })
          .then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                items: [
                  {
                    id: variantId,
                    quantity: 1,
                    properties: (function () {
                      var props = {};
                      props[GIFT_PROPERTY_KEY] = GIFT_PROPERTY_VALUE;
                      return props;
                    })(),
                  },
                ],
              }),
            });
          })
          .then(function () {
            closeModal();
            if (formToResume) {
              submitFormBypassingGate(formToResume, submitterToResume);
            } else if (window.location.pathname.indexOf('/cart') === 0) {
              window.location.reload();
            } else {
              refresh();
            }
          })
          .catch(function (err) {
            console.error('Free cologne add failed', err);
            btn.disabled = false;
            btn.textContent = 'Select';
          });
      });
    }
  }

  // mode: 'browse' (clicked the widget button, nothing pending) | 'compulsory'
  // (blocking an actual checkout submit, pendingForm is set) | 'auto' (fired on
  // its own the instant the cart qualifies, nothing blocked, just a heads-up).
  function openModal(mode) {
    var modal = ensureModal();
    modal.hidden = false;
    var title = document.getElementById('free-cologne-modal-title');
    var intro = document.getElementById('free-cologne-modal-intro');

    if (mode === 'compulsory') {
      title.textContent = 'You Qualify for a FREE Cologne!';
      intro.textContent = 'Pick the one you’d like to continue to checkout.';
    } else if (mode === 'auto') {
      title.textContent = 'You Just Unlocked a FREE Cologne!';
      intro.textContent = 'Pick yours now, or close this and choose it later from your cart — just don’t forget before you check out.';
    } else {
      title.textContent = 'Choose Your Free Cologne';
      intro.textContent = '';
    }
    document.body.classList.add('free-cologne-modal-open');
  }

  function closeModal() {
    var modal = document.getElementById('free-cologne-modal');
    if (modal) modal.hidden = true;
    document.body.classList.remove('free-cologne-modal-open');
    pendingForm = null;
    pendingSubmitter = null;
  }

  // ---------- Shared render logic for every widget on the page ----------

  function findGiftLineItem(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (item.properties && item.properties[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE) {
        return item;
      }
    }
    return null;
  }

  function setChooseButtonVisible(btn, visible, label) {
    if (visible) {
      btn.hidden = false;
      btn.style.removeProperty('display');
      btn.classList.add('free-cologne-promo__button--qualified');
      if (label) btn.textContent = label;
    } else {
      btn.hidden = true;
      btn.style.display = 'none';
      btn.classList.remove('free-cologne-promo__button--qualified');
    }
  }

  // Resolves to the cart's eligible-for-the-promo subtotal (Rand): the sum of
  // every line that is currently at full price, exactly mirroring the rule
  // the real "Spend R7,000, Get a Free Cologne" discount applies server-side.
  // A line counts as discounted — and is excluded — the moment /cart.js says
  // its final price is below its original price, no matter what put it there:
  // a native compare-at sale, a "Buy X Get Y" discount, or a bundle app like
  // Fast Bundle applying its own cart-level pricing. Reading this straight off
  // /cart.js (rather than re-fetching each product) means it can't miss a
  // discount mechanism the way a compare-at-only check could, and needs no
  // extra network round-trips.
  function computeEligibleSubtotal(cart) {
    var eligible = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var originalLinePrice = typeof item.original_line_price === 'number' ? item.original_line_price : item.price * item.quantity;
      var finalLinePrice = typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price;
      var isDiscounted = (item.total_discount > 0) || finalLinePrice < originalLinePrice;
      if (!isDiscounted) eligible += finalLinePrice;
    }
    return Promise.resolve(eligible / 100);
  }

  // Resolves to { total, eligibleTotal, giftItem, giftIsFree, qualifies }.
  // `qualifies` is based on eligibleTotal (full-price items only), matching
  // the real discount; `total` is the raw cart subtotal, kept only so the
  // UI can explain the difference when a sale item is holding someone back.
  function computeCartState(cart) {
    var total = cart.total_price / 100;
    var giftItem = findGiftLineItem(cart);
    // NOTE: item.price is the item's nominal/original price and is NOT
    // discount-adjusted by Shopify's /cart.js — it stays the same whether or
    // not the "Buy X Get Y" discount applied. item.line_price IS the real,
    // post-discount amount the customer will actually pay for this line, so
    // that's what "is it genuinely free right now" has to check.
    var giftIsFree = !!giftItem && giftItem.line_price === 0;

    return computeEligibleSubtotal(cart).then(function (eligibleTotal) {
      return {
        total: total,
        eligibleTotal: eligibleTotal,
        giftItem: giftItem,
        giftIsFree: giftIsFree,
        qualifies: eligibleTotal >= THRESHOLD,
      };
    });
  }

  function renderWidgets(state) {
    var widgets = document.querySelectorAll('[data-free-cologne-widget]');
    for (var i = 0; i < widgets.length; i++) {
      var widget = widgets[i];
      widget.hidden = false;
      var messageEl = widget.querySelector('[data-free-cologne-message]');
      var btn = widget.querySelector('[data-free-cologne-choose-btn]');
      if (!messageEl || !btn) continue;

      if (state.qualifies) {
        if (state.giftIsFree) {
          messageEl.innerHTML =
            'You qualify for a FREE Cologne! Your gift: <strong>' + state.giftItem.product_title + '</strong>';
          setChooseButtonVisible(btn, true, 'Change Your Cologne');
        } else {
          messageEl.textContent = 'You qualify for a FREE Cologne — pick yours now!';
          setChooseButtonVisible(btn, true, 'Choose Your Cologne');
        }
      } else {
        var remaining = THRESHOLD - state.eligibleTotal;
        var html = "You're " + formatMoney(remaining) + ' away from a FREE Cologne.';
        // Sale items don't count toward this — if that's why the gap looks
        // bigger than the cart subtotal suggests, say so, so it isn't a
        // surprise at checkout.
        if (state.eligibleTotal < state.total - 0.01) {
          html += '<br><small>Sale items in your cart don’t count toward this total.</small>';
        }
        if (state.giftItem && !state.giftIsFree) {
          html +=
            '<br><small>' +
            (state.giftItem.product_title || 'Your selected cologne') +
            ' is in your cart at full price (' +
            formatMoney(state.giftItem.price / 100) +
            '). Add ' +
            formatMoney(remaining) +
            ' more of full-price items to make it free.</small>';
        }
        messageEl.innerHTML = html;
        setChooseButtonVisible(btn, false);
      }
    }

    lastCartState = state;
    maybeAutoPrompt(state);
    return state;
  }

  // Real-world gap this closes: Stitch Express / Shop Pay / other one-click
  // "dynamic checkout" buttons (available on both product pages and the cart
  // page) skip the cart page's own "Check out" button entirely, so
  // attachGate() never gets a chance to run for those customers — confirmed
  // from a real order that qualified (R7,780) but checked out via Stitch
  // Express with no cologne ever added. That is a hard platform limitation:
  // those buttons cannot be intercepted by theme JS (no Plus-only checkout
  // extensibility on this plan, and several are cross-origin iframes browser
  // security won't let any page script touch). The best available fix is to
  // stop waiting for a checkout click at all — surface the offer the moment
  // the cart qualifies, on whatever page that happens, before the customer
  // has a chance to reach for any checkout button, express or otherwise.
  // Once per browser session (not on every single page nav) so it doesn't
  // nag; not compulsory, since there's nothing to block yet at this point.
  var AUTO_PROMPT_KEY = 'freeCologneAutoPromptShown';

  function maybeAutoPrompt(state) {
    if (!state.qualifies || state.giftIsFree) return;
    var modal = document.getElementById('free-cologne-modal');
    if (modal && !modal.hidden) return; // don't stack on top of an already-open modal
    try {
      if (sessionStorage.getItem(AUTO_PROMPT_KEY)) return;
      sessionStorage.setItem(AUTO_PROMPT_KEY, '1');
    } catch (err) {
      // sessionStorage unavailable (privacy mode, etc.) — fall through and
      // still show it; worst case it shows more than once for that visitor.
    }
    openModal('auto');
  }

  function refresh() {
    return fetch('/cart.js')
      .then(function (r) {
        return r.json();
      })
      .then(computeCartState)
      .then(renderWidgets)
      .catch(function () {});
  }

  // This theme's own scripts (and several installed apps) call Shopify's cart
  // endpoints directly and don't reliably announce it afterwards — e.g. adding
  // more of a product already in the cart, or using the quantity +/- steppers,
  // was not updating this widget, which is why the button could stay hidden
  // even after the cart genuinely passed R7,000. Patching fetch is a
  // stone-cold-reliable way to notice every cart change regardless of which
  // script made it, without depending on any particular theme/app wiring it
  // up correctly.
  function patchFetchForCartRefresh() {
    if (window.fetch.__freeCologneCartPatched) return;
    var originalFetch = window.fetch;
    var patched = function () {
      var result = originalFetch.apply(this, arguments);
      try {
        var target = arguments[0];
        var url = typeof target === 'string' ? target : (target && target.url) || '';
        if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
          result.then(refresh).catch(function () {});
        }
      } catch (err) {
        // ignore — never let this break the site's own fetch calls
      }
      return result;
    };
    patched.__freeCologneCartPatched = true;
    window.fetch = patched;
  }

  // ---------- Checkout gate ----------

  // `submitter` is the actual button the shopper clicked (e.g. the "Check out"
  // button with name="checkout"). Shopify's /cart endpoint decides what to do
  // next based on THAT button's name/value, so it must be resent — plain
  // form.requestSubmit()/form.submit() with no submitter silently drops it and
  // the cart just re-renders the cart page instead of proceeding to checkout.
  function submitFormBypassingGate(form, submitter) {
    form[GATE_BYPASS_FLAG] = true;
    if (typeof form.requestSubmit === 'function') {
      try {
        form.requestSubmit(submitter || undefined);
        return;
      } catch (err) {
        // fall through to the manual fallback below
      }
    }
    if (submitter && submitter.name) {
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = submitter.name;
      hidden.value = submitter.value || '';
      form.appendChild(hidden);
    }
    form.submit();
  }

  function attachGate(form) {
    if (!form || form.__freeCologneGated) return;
    form.__freeCologneGated = true;
    form.addEventListener('submit', function (e) {
      if (form[GATE_BYPASS_FLAG]) {
        form[GATE_BYPASS_FLAG] = false;
        return; // let this one through, it's a resume after the modal resolved
      }
      var submitter = e.submitter;
      e.preventDefault();
      fetch('/cart.js')
        .then(function (r) {
          return r.json();
        })
        .then(computeCartState)
        .then(function (state) {
          renderWidgets(state);
          if (!state.qualifies || state.giftIsFree) {
            submitFormBypassingGate(form, submitter);
            return;
          }
          pendingForm = form;
          pendingSubmitter = submitter;
          openModal('compulsory');
        })
        .catch(function () {
          submitFormBypassingGate(form, submitter);
        });
    });
  }

  function attachAllGates() {
    attachGate(document.getElementById('cart'));
    attachGate(document.getElementById('cart-notification-form'));
    attachGate(document.querySelector('cart-drawer form[action$="/cart"]'));
  }

  // ---------- Wire up any static widgets already in the page markup ----------

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-free-cologne-choose-btn]')) {
      openModal('browse');
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
  }

  function init() {
    ensureModal();
    refresh();
    attachAllGates();
    patchFetchForCartRefresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
